import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface BusinessHours {
  day: string;
  hours: string;
}

interface OfficeInfo {
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  businessHours: BusinessHours[];
  parkingInfo: string;
  accessibilityInfo: string;
}

interface OfficeInformationProps {
  officeInfo: OfficeInfo;
}

const OfficeInformation: React.FC<OfficeInformationProps> = ({ officeInfo }) => {
  return (
    <section className="py-16 lg:py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground mb-4">
            Visit Our Office
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We welcome you to visit our office for in-person consultations. Here's everything you need to know.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-card rounded-xl p-8 shadow-subtle border border-border">
              <div className="flex items-start space-x-4 mb-6">
                <div className="p-3 bg-accent/10 rounded-lg">
                  <Icon name="MapPinIcon" size={28} className="text-accent" variant="solid" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-2">
                    Office Address
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {officeInfo.address}<br />
                    {officeInfo.city}, {officeInfo.state} {officeInfo.zip}
                  </p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${officeInfo.address}, ${officeInfo.city}, ${officeInfo.state} ${officeInfo.zip}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-accent font-semibold mt-4 hover:text-gold-dark transition-colors duration-300"
                  >
                    <span>Get Directions</span>
                    <Icon name="ArrowTopRightOnSquareIcon" size={18} variant="outline" />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl p-8 shadow-subtle border border-border">
              <div className="flex items-start space-x-4 mb-6">
                <div className="p-3 bg-accent/10 rounded-lg">
                  <Icon name="ClockIcon" size={28} className="text-accent" variant="solid" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-4">
                    Business Hours
                  </h3>
                  <div className="space-y-3">
                    {officeInfo.businessHours.map((schedule, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-border last:border-b-0">
                        <span className="font-medium text-foreground">{schedule.day}</span>
                        <span className="text-muted-foreground">{schedule.hours}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground">
                    <Icon name="InformationCircleIcon" size={16} className="inline mr-1" variant="outline" />
                    Emergency consultations available by appointment
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl p-8 shadow-subtle border border-border">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-accent/10 rounded-lg">
                  <Icon name="TruckIcon" size={28} className="text-accent" variant="solid" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-2">
                    Parking Information
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {officeInfo.parkingInfo}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl p-8 shadow-subtle border border-border">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-accent/10 rounded-lg">
                  <Icon name="UserGroupIcon" size={28} className="text-accent" variant="solid" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-2">
                    Accessibility
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {officeInfo.accessibilityInfo}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl overflow-hidden shadow-subtle border border-border h-[600px]">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title="Lubbert Associates LLC Office Location"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=33.7490,-84.3880&z=15&output=embed"
              className="border-0"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfficeInformation;